<template>
  <BaseModal
    :model-value="modelValue"
    :close-on-esc="true"
    :close-on-background-click="true"
    :manage-focus="false"
    :title-id="titleId"
    card-class="confirm-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="cancel"
  >
    <template #header>
      <p :id="titleId" class="modal-card-title">{{ title }}</p>
    </template>

    <template #default>
      <p :id="messageId">
        <slot>{{ message }}</slot>
      </p>
    </template>

    <template #footer>
      <div class="is-flex is-flex-direction-column is-align-items-stretch is-fullwidth">
        <label v-if="showDontAskAgain" class="checkbox is-size-7 mb-3">
          <input v-model="dontAskAgain" type="checkbox" />
          {{ dontAskAgainText }}
        </label>
        <div class="is-flex is-justify-content-flex-end">
          <button ref="cancelButtonRef" class="button" :disabled="isLoading" @click="cancel">
            {{ cancelText }}
          </button>
          <button
            ref="confirmButtonRef"
            class="button ml-2"
            :class="[confirmClass, { 'is-loading': isLoading }]"
            :disabled="isLoading"
            @click="confirm"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
  import { ref, watch, nextTick } from "vue";
  import BaseModal from "./base-modal.vue";

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "Confirm" },
    message: { type: String, default: "" },
    confirmText: { type: String, default: "Confirm" },
    cancelText: { type: String, default: "Cancel" },
    confirmClass: { type: String, default: "is-danger" },
    showDontAskAgain: { type: Boolean, default: false },
    dontAskAgainText: { type: String, default: "Don't ask me again for the next hour" },
    isLoading: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update:modelValue", "confirm", "cancel", "dont-ask-again"]);

  const dontAskAgain = ref(false);
  const confirmButtonRef = ref(null);
  const cancelButtonRef = ref(null);
  const previousActiveElement = ref(null);

  const instanceId = Math.random().toString(36).slice(2, 9);
  const titleId = `confirm-dialog-title-${instanceId}`;
  const messageId = `confirm-dialog-message-${instanceId}`;

  function confirm() {
    if (dontAskAgain.value) {
      emit("dont-ask-again");
    }
    emit("confirm");
    emit("update:modelValue", false);
  }

  function cancel() {
    emit("cancel");
    emit("update:modelValue", false);
  }

  watch(
    () => props.modelValue,
    (isOpen) => {
      if (isOpen) {
        previousActiveElement.value = document.activeElement;
        nextTick(() => {
          confirmButtonRef.value?.focus();
        });
      } else {
        nextTick(() => {
          if (
            previousActiveElement.value &&
            typeof previousActiveElement.value.focus === "function"
          ) {
            previousActiveElement.value.focus();
          }
        });
      }
    }
  );
</script>

<style scoped>
  .confirm-dialog {
    max-width: 400px;
    width: 100%;
  }
</style>
